import axios from 'axios'
import { apiURL } from 'src/consumer/services'
import { shouldUseMocks } from './mockApi'
import * as mock from './mockApi/dietApi'
import { patientGet, patientPost } from './patientApi'

const BASE = '/api/secure/me/diet'
const CTX = { requirePatientContext: true }

export async function getPlan(profile) {
  if (shouldUseMocks()) return mock.getPlan(profile)
  const res = await patientGet(`${BASE}/plan`, CTX)
  return res?.data || null
}

export async function getToday(profile) {
  if (shouldUseMocks()) return mock.getToday(profile)
  const res = await patientGet(`${BASE}/today`, CTX)
  return res?.data || null
}

export async function log(profile, body) {
  if (shouldUseMocks()) return mock.log(profile, body)
  const res = await patientPost(`${BASE}/log`, body, CTX)
  return res?.data
}

export async function listLog(profile, from, to) {
  if (shouldUseMocks()) return mock.listLog(profile, from, to)
  const res = await patientGet(`${BASE}/log`, { ...CTX, params: { from, to } })
  return res?.data || []
}

/**
 * Search the food catalog. Empty {@code q} returns the most-popular items
 * (optionally narrowed by category) so the picker has useful first-render
 * content. Non-empty {@code q} does a token-prefix match server-side.
 *
 * Dietary filtering: by default the server applies the active patient's
 * stored {@code dietaryPreference}. Pass {@code allDiets: true} to bypass
 * the filter (used by the FoodLogDialog "Show all" toggle), or
 * {@code diet: 'VEGETARIAN'} for an explicit override.
 *
 * @param {object} profile  active PatientProfile
 * @param {object} [opts]   { q?: string, category?: string, limit?: number,
 *                            diet?: string, allDiets?: boolean }
 * @returns {Promise<Array<{
 *   code: string, displayName: string, nameTranslations?: object,
 *   category: string, cuisine?: string, tags?: string[],
 *   defaultQuantityLabel?: string, approxCalories?: number
 * }>>}
 */
export async function searchFoodCatalog(profile, opts = {}) {
  if (shouldUseMocks()) return mock.searchFoodCatalog(profile, opts)
  const res = await patientGet(`${BASE}/food-catalog/search`, {
    ...CTX,
    profileId: profile?.id,
    params: {
      q: opts.q || undefined,
      category: opts.category || undefined,
      limit: opts.limit || undefined,
      diet: opts.diet || undefined,
      allDiets: opts.allDiets ? 'true' : undefined,
    },
  })
  return Array.isArray(res?.data) ? res.data : []
}

/**
 * Upload a meal photo via multipart form. Returns { fileId, url, mimeType,
 * sizeBytes }. The patient-portal frontend stores the {@code url} on the
 * subsequent {@code log()} call so the photo is associated with the meal
 * entry server-side.
 *
 * Mocks: returns a synthetic data: URL so the dev UX is exercisable
 * without a real backend roundtrip.
 *
 * @param {object} profile  active PatientProfile
 * @param {File} file       browser File / Blob from <input type="file"> or
 *                          a captured camera blob
 * @param {object} [opts]   { signal?: AbortSignal,
 *                            onProgress?: (pct: 0..100) => void }
 */
export async function uploadDietPhoto(profile, file, opts = {}) {
  if (!file) throw new Error('file is required')
  if (shouldUseMocks()) return mock.uploadDietPhoto(profile, file)
  const form = new FormData()
  form.append('file', file)
  // Attach patientId via query param like every other /me/* call —
  // patientApi can't be used here because it doesn't accept FormData;
  // we do the multipart POST directly with axios.
  const url = `${apiURL}${BASE}/photo`
  const token = (() => {
    try { return window.localStorage.getItem('token') || null } catch (e) { return null }
  })()
  const res = await axios.post(url, form, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    params: profile?.id ? { patientId: profile.id } : {},
    onUploadProgress: opts.onProgress
      ? (evt) => {
          if (!evt.total) return
          opts.onProgress(Math.round((evt.loaded / evt.total) * 100))
        }
      : undefined,
    signal: opts.signal,
  })
  return res?.data || null
}
